import { useEffect, useState, useMemo } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { postAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ChevronLeft, ChevronRight, Plus, PenSquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PostDetailSheet } from '../components/PostDetailSheet';
import { formatDateLong } from '../lib/dateFormat';

const statusColors = {
  draft: 'bg-secondary text-secondary-foreground',
  in_review: 'bg-amber-500/15 text-amber-400 border border-amber-500/20',
  approved: 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20',
  scheduled: 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/20',
  published: 'bg-primary/15 text-teal-400 border border-primary/20',
  rejected: 'bg-rose-500/15 text-rose-400 border border-rose-500/20',
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export default function ContentCalendarPage() {
  const { currentWorkspace } = useWorkspace();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month');
  const [selectedPost, setSelectedPost] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (currentWorkspace?.id) {
      setLoading(true);
      postAPI.list(currentWorkspace.id)
        .then(res => setPosts(res.data.items || []))
        .catch(err => console.error(err))
        .finally(() => setLoading(false));
    }
  }, [currentWorkspace?.id]);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const calendarDays = useMemo(() => {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const prevMonthDays = new Date(year, month, 0).getDate();
    const days = [];

    // Previous month days
    for (let i = firstDay - 1; i >= 0; i--) {
      days.push({ day: prevMonthDays - i, isCurrentMonth: false, date: new Date(year, month - 1, prevMonthDays - i) });
    }
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ day: i, isCurrentMonth: true, date: new Date(year, month, i) });
    }
    // Next month days to fill grid
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ day: i, isCurrentMonth: false, date: new Date(year, month + 1, i) });
    }
    return days;
  }, [year, month]);

  const getPostsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return posts.filter(p => {
      const postDate = (p.scheduled_at || p.created_at || '').split('T')[0];
      return postDate === dateStr;
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
  };

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToToday = () => setCurrentDate(new Date());

  return (
    <>
    <div className="p-6 space-y-6 animate-fade-in-up">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-semibold">Content Calendar</h1>
          <p className="text-sm text-muted-foreground mt-1">Plan and schedule your content across platforms.</p>
        </div>
        <Button onClick={() => navigate('/compose')} data-testid="calendar-create-post-button">
          <Plus className="h-4 w-4 mr-2" /> New Post
        </Button>
      </div>

      {/* Calendar Controls */}
      <Card className="bg-card/60 backdrop-blur">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={prevMonth}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <h2 className="text-lg font-display font-semibold min-w-[200px] text-center">
                {MONTHS[month]} {year}
              </h2>
              <Button variant="ghost" size="icon" onClick={nextMonth}>
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={goToToday}>Today</Button>
            </div>
          </div>

          {/* Day Headers */}
          <div className="grid grid-cols-7 mb-1">
            {DAYS.map(d => (
              <div key={d} className="text-center text-xs font-medium text-muted-foreground py-2">{d}</div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-px bg-border rounded-lg overflow-hidden">
            {calendarDays.map((cell, i) => {
              const dayPosts = getPostsForDate(cell.date);
              return (
                <div
                  key={i}
                  className={`min-h-[100px] p-1.5 bg-card ${
                    !cell.isCurrentMonth ? 'opacity-40' : ''
                  } ${isToday(cell.date) ? 'ring-2 ring-primary ring-inset' : ''}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs font-medium ${
                      isToday(cell.date) ? 'text-primary' : 'text-muted-foreground'
                    }`}>
                      {cell.day}
                    </span>
                    {cell.isCurrentMonth && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5 opacity-0 hover:opacity-100 focus:opacity-100"
                        onClick={() => navigate(`/compose?date=${cell.date.toISOString().split('T')[0]}`)}
                      >
                        <Plus className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                  <div className="space-y-0.5">
                    {dayPosts.slice(0, 3).map(post => (
                      <div
                        key={post.id}
                        className="text-[10px] px-1.5 py-0.5 rounded truncate bg-primary/10 text-primary cursor-pointer hover:bg-primary/20 transition-colors"
                        onClick={() => setSelectedPost(post)}
                        data-testid={`calendar-post-${post.id}`}
                      >
                        {post.title}
                      </div>
                    ))}
                    {dayPosts.length > 3 && (
                      <p className="text-[10px] text-muted-foreground px-1">+{dayPosts.length - 3} more</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Posts List */}
      <Card className="bg-card/60 backdrop-blur">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">All Posts ({posts.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {posts.length === 0 ? (
            <div className="text-center py-8">
              <PenSquare className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No posts yet. Create your first!</p>
            </div>
          ) : (
            <div className="space-y-2">
              {posts.map(post => (
                <div
                  key={post.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-secondary/30 hover:bg-secondary/50 transition-colors cursor-pointer"
                  onClick={() => setSelectedPost(post)}
                  data-testid={`post-list-item-${post.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{post.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      {(post.platforms || []).map(p => (
                        <span key={p} className={`text-xs platform-${p} capitalize`}>{p}</span>
                      ))}
                      <span className="text-xs text-muted-foreground">
                        {post.scheduled_at ? `Scheduled: ${formatDateLong(post.scheduled_at)}` : formatDateLong(post.created_at)}
                      </span>
                    </div>
                  </div>
                  <Badge className={`text-xs ${statusColors[post.status] || 'bg-secondary'}`}>
                    {post.status?.replace('_', ' ')}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>

      <PostDetailSheet
        post={selectedPost}
        open={!!selectedPost}
        onOpenChange={open => { if (!open) setSelectedPost(null); }}
        onStatusChange={() => {
          setSelectedPost(null);
          if (currentWorkspace?.id) {
            postAPI.list(currentWorkspace.id).then(r => setPosts(r.data)).catch(() => {});
          }
        }}
      />
    </>
  );
}
